import { Button } from "@/components/ui/Button";
import { useEffect, useState } from "react";
import { BudgetCard } from "@/components/budget/BudgetCard";
import { CreateBudgetDialog } from "@/components/budget/CreateBudgetDialog";
import { Plus, CloudUpload } from "lucide-react";
import { useBudget } from "@/hooks/use-budget";
import { Divider, Alert } from "@mui/material";
import { BudgetUpdateHandler } from "@/components/budget/BudgetUpdateHandler";
import { useToastContext } from "@/context/ToastContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { useParams, Navigate } from "react-router-dom";
import ConnectionInfo from "@/components/ConnectionInfo";
import { notFoundUrl } from "@/routes/urls";
import { isValidGUID } from "@/utils/validation";
import Background from "@/components/ui/Background";
import { H1 } from "@/components/ui/Typography";
import Card from "@/components/ui/Card";

const BudgetModule = () => {
  const { showToast } = useToastContext();
  const { clientId, connectionId } = useParams<{ clientId: string; connectionId: string }>();

  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [selectedBudgetName, setSelectedBudgetName] = useState<string | null>(null);
  const [selectedBudgetIsActive, setSelectedBudgetIsActive] = useState<boolean | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deletingBudgetId, setDeletingBudgetId] = useState<string | null>(null);
  const [settingActiveBudgetId, setSettingActiveBudgetId] = useState<string | null>(null);

  const {
    budgets,
    isLoadingBudgets,
    createNewBudget,
    isCreating,
    deleteBudget,
    setBudgetActive,
    updateBudgetName,
    createSuccess,
    createError,
    deleteSuccess,
    deleteError,
    publishBudgets,
    isPublishing
  } = useBudget(clientId, connectionId);

  useEffect(() => {
    if (createSuccess) {
      showToast({
        title: "Success",
        description: "Budget created successfully",
        variant: "success",
      });
    }
  }, [createSuccess, showToast]);

  useEffect(() => {
    if (createError) {
      showToast({
        title: "Error",
        description: "Failed to create budget",
        variant: "error",
      });
    }
  }, [createError, showToast]);

  useEffect(() => {
    if (deleteSuccess) {
      showToast({
        title: "Success",
        description: "Budget deleted successfully",
        variant: "success",
      });
    }
  }, [deleteSuccess, showToast]);

  useEffect(() => {
    if (deleteError) {
      showToast({
        title: "Error",
        description: "Failed to delete budget",
        variant: "error",
      });
    }
  }, [deleteError, showToast]);

  if (!clientId || !connectionId) {
    return <Navigate to={notFoundUrl} />;
  }

  const handleCreateBudget = async (
    name: string,
    startMonth: string,
    duration: "Year" | "Quarter",
    growth: number
  ) => {

    if (!isValidGUID(connectionId)) {
      showToast({
        title: "Error",
        description: "Invalid connection ID format",
        variant: "error",
      });
      return;
    }

    const [year, month] = startMonth.split("-").map(Number);
    const monthTo = duration === "Year" ? 12 : month + 2;

    createNewBudget({
      clientId: clientId,
      connectionIds: [connectionId] as string[],
      name,
      yearFrom: year,
      monthFrom: month,
      yearTo: year,
      monthTo: monthTo,
      projectionPercentage: growth,
    });
  };

  const handleDeleteBudget = async (id: string) => {
    setDeletingBudgetId(id);
    try {
      await deleteBudget(id);
    } finally {
      setDeletingBudgetId(null);
    }
  };

  const handleSetBudgetActive = async (id: string) => {
    setSettingActiveBudgetId(id);
    try {
      await setBudgetActive({ clientId: clientId, connectionId: connectionId, budgetId: id });
    } finally {
      setSettingActiveBudgetId(null);
    }
    console.log('Set Budget Active:', id);
  }

  if (isLoadingBudgets) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const renderContent = () => {
    if (isCreating) {
      return (
        <div className="relative">
          <div className="absolute inset-0 flex justify-center items-center h-[80vh] z-50 bg-white/75">
            <LoadingSpinner message={"Creating budget. That may take few minutes..."} />
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {budgets?.map((budget) => (
              <BudgetCard
                key={budget.id}
                id={budget.id}
                name={budget.name ?? ""}
                isActive={budget.isActive}
                isSelected={budget.id === selectedBudgetId}
                isDeleting={budget.id === deletingBudgetId}
                isSettingActive={budget.id === settingActiveBudgetId}
                onSetActive={() => handleSetBudgetActive(budget.id)}
                onDelete={() => handleDeleteBudget(budget.id)}
                onSelect={() => {
                  setSelectedBudgetId(budget.id);
                  setSelectedBudgetName(budget.name);
                  setSelectedBudgetIsActive(budget.isActive);
                }}
                onUpdateName={(newName) => {
                  console.log(newName);
                }}
              />
            ))}
          </div>
        </div>
      );
    }

    if (!budgets || budgets.length === 0) {
      return (
        <Alert severity="info" className="mb-4">
          No budgets found. Create your first budget to get started.
        </Alert>
      );
    }

    return (
      <div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {budgets.map((budget) => (
            <BudgetCard
              key={budget.id}
              id={budget.id}
              name={budget.name ?? ""}
              isActive={budget.isActive}
              isDeleting={budget.id === deletingBudgetId}
              isSelected={budget.id === selectedBudgetId}
              onSetActive={() => handleSetBudgetActive(budget.id)}
              onDelete={() => handleDeleteBudget(budget.id)}
              onSelect={() => {
                setSelectedBudgetId(budget.id);
                setSelectedBudgetName(budget.name);
                setSelectedBudgetIsActive(budget.isActive);
                console.log('Selected Budget:', selectedBudgetId);

              }}
              onUpdateName={(newName) => {
                updateBudgetName({ id: budget.id, newName: newName, budget: budget });
              }}
            />
          ))}
        </div>
        <div className="h-6"></div>
        <Divider />
        <div className="h-3"></div>
        <Card className="border border-palette-dark-gray rounded-lg" hoverable={false}>
          {selectedBudgetId && (
            <BudgetUpdateHandler
              clientId={clientId}
              connectionId={connectionId}
              budgetId={selectedBudgetId}
              budgetName={selectedBudgetName}
              isActive={selectedBudgetIsActive}
            />
          )}
          {!selectedBudgetId && (
            <Alert severity="info" className="mb-4">
              No budget selected.
            </Alert>
          )}
        </Card>
      </div>
    );
  };

  return (
    <Background className="mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <H1>Budgets</H1>
        <div className="flex justify-end items-center gap-4">
          <ConnectionInfo />
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            startIcon={<Plus className="h-4 w-4" />}
            disabled={isCreating || !!settingActiveBudgetId || !!deletingBudgetId || isPublishing}
          >
            {isCreating ? <LoadingSpinner /> : "Create"}
          </Button>
          <Button
            onClick={async () => { await publishBudgets({ clientId: clientId, connectionId: connectionId }) }}
            startIcon={<CloudUpload className="h-4 w-4" />}
            disabled={!budgets.length || isCreating || !!settingActiveBudgetId || !!deletingBudgetId || isPublishing}
            variant={"outlined"}
          >
            {isPublishing ? <LoadingSpinner /> : "Publish"}
          </Button>
        </div>
      </div>

      {renderContent()}

      <CreateBudgetDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        onCreateBudget={handleCreateBudget}
      />
    </Background>
  );
};

export default BudgetModule;
